import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import {
  Leg,
  legContainsStop,
  Stop,
  StopContext,
} from "@vygruppen/vy-train-map";
import { getBackendUrl } from "api/common";
import { queryFnGET } from "api/tanStackQuery/helpers";
import { useFeatureFlag } from "app/FeatureFlags/useFeatureFlags";
import {
  GlobalFormSchema,
  globalFormSchema,
} from "features/CenterContent/RoleContent/GlobalEvents/types/formSchema";
import {
  GlobalEventResponse,
  globalEventsResponseSchema,
} from "features/CenterContent/RoleContent/GlobalEvents/types/responseSchema";
import {
  formToGlobalInformationRequest,
  responseToGlobalInformationForm,
} from "features/CenterContent/RoleContent/GlobalEvents/types/transformers";
import { usePostGlobalInfo } from "features/CenterContent/RoleContent/GlobalEvents/usePostGlobalInfo";
import {
  buildEvaluationFormUrl,
  contextsForEvaluationForm,
} from "features/CenterContent/RoleContent/InternalMessage/schema/buildEvaluationFormUrl";
import { InternalMessagesFormField } from "features/CenterContent/RoleContent/InternalMessage/schema/formSchema";
import { createInternalMessage } from "features/CenterContent/RoleContent/InternalMessage/utils/queries";
import {
  handleInternalMessageSubmit,
  useFormInternalMessage,
} from "features/CenterContent/RoleContent/InternalMessage/utils/useForm";
import { InfrastructureModalContent } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/infrastructureEventModal/InfrastructureModalContent";
import { MajorEventWarning } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/infrastructureEventModal/MajorEventWarning";
import { ReferencedGlobalEvent } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/infrastructureEventModal/ReferencedGlobalEvent";
import { usePostInfrastructureEvent } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/infrastructureEventModal/hooks/usePostInfrastructureEvent";
import {
  formSchema as infraFormSchema,
  InfrastructureForm as InfraFormSchema,
} from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/types/formSchema";
import {
  formToRequest,
  getCurrentCalendarDateTime,
  responseToForm,
} from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/types/transformers";
import { getStretchName } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/stretchBuilderTexts";
import { useDepublishInformation } from "features/OperationalInformation/useDepublishInformation";
import { FC, useState } from "react";
import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ActionModal } from "shared/components/ActionModal";
import {
  DropsStaticAlert,
  Skeleton,
  Stack,
  Text,
} from "shared/components/Spor";
import { useFormVerbose } from "shared/form/useFormVerbose";
import { useCountryCodeForDropsRole } from "shared/hooks/useCountryCodeForDropsRole";
import {
  isInfrastructureStretchClosedEvent,
  isInfrastructureStretchPartiallyClosedEvent,
  isPlannedInfraStructureEvent,
} from "shared/utils/infrastructureEvent";
import { useDropsRole } from "stores/useDropsRole";
import { useOpenEditFields } from "stores/useOpenEditFields";
import { useStretchBuilder } from "stores/useStretchBuilder";

const FIELDS_AFFECTING_TEXT = [
  "stretchName",
  "reason",
  "action",
  "consequence",
  "prognosis",
  "prognosisTime",
  "event",
];

const legsIncludeStop = (legs: Leg[], stop: Stop) =>
  legs.some((leg) => legContainsStop(leg, stop));

const OSL: Stop = {
  name: "Oslo S",
  countryCode: "NO",
  stopIds: [{ id: "OSL", context: StopContext.JBV }],
};

export const GLOBAL_INFO_RELATED_TO_INCIDENT_QUERY_KEY =
  "globalInfoRelatedToIncident";

export const InfrastructureEvent: FC = () => {
  const { t } = useTranslation();
  const [
    clickedStops,
    selectedLegs,
    allStopsSelected,
    stretchBuilderModalState,
    closeAndResetStretchBuilder,
  ] = useStretchBuilder((state) => [
    state.clickedStops,
    state.selectedLegs,
    state.allStopsSelected,
    state.stretchBuilderModalState,
    state.closeAndResetStretchBuilder,
  ]);

  const { isDriftledningen } = useDropsRole();

  const prevInfrastructureEvent =
    (stretchBuilderModalState.type === "infrastructure" &&
      stretchBuilderModalState.eventInfo) ||
    undefined;

  const isEditMode = !!prevInfrastructureEvent;

  const {
    data: globalInformationData,
    isSuccess: globalInfoIsSuccess,
    isPending: globaInfoIsPending,
    isFetching: globalInfoIsFetching,
    isError: globalInfoIsError,
  } = useQuery({
    queryKey: [
      GLOBAL_INFO_RELATED_TO_INCIDENT_QUERY_KEY,
      prevInfrastructureEvent?.incidentId,
    ],
    queryFn: ({ signal }) =>
      queryFnGET<GlobalEventResponse[]>({
        signal,
        url: `${getBackendUrl()}/global-information/by-incident-id/${prevInfrastructureEvent!.incidentId}`,
      }).then((res) => globalEventsResponseSchema.parse(res)),
    enabled: !!prevInfrastructureEvent && !isDriftledningen(),
    staleTime: 0,
  });

  const globalInformation = globalInformationData
    ? globalInformationData.filter(
        (globalEvent) => globalEvent.type === "GLOBAL_GENERAL_WARNING",
      )
    : [];
  const relatedGlobalInformation =
    globalInformation.length > 0 ? globalInformation[0] : undefined;

  const countryCodeForRole = useCountryCodeForDropsRole();
  const infrastructureDefaultValues = responseToForm(
    prevInfrastructureEvent,
    countryCodeForRole,
  );

  const internalMessagesFormMethods = useFormInternalMessage(
    prevInfrastructureEvent?.internalMessages,
  );
  const onInternalMessagesSubmit = handleInternalMessageSubmit(
    internalMessagesFormMethods,
  );

  const infrastructureFormMethods = useFormVerbose<InfraFormSchema>({
    resolver: zodResolver(infraFormSchema),
    defaultValues: infrastructureDefaultValues,
  });

  const [createEvaluationForm, setCreateEvaluationForm] = useState(false);
  const hasEvaluationForm =
    prevInfrastructureEvent?.internalMessages?.some(
      (message) => !!message.evaluationForm,
    ) ?? false;

  const evaluationFormEnabled = useFeatureFlag("evaluationForm").enabled;
  const showEvaluationForm =
    evaluationFormEnabled &&
    (hasEvaluationForm ||
      isInfrastructureStretchClosedEvent(
        infrastructureFormMethods.getValues("infrastructureForm.type"),
      ) ||
      isInfrastructureStretchPartiallyClosedEvent(
        infrastructureFormMethods.getValues("infrastructureForm.type"),
      )) &&
    !isDriftledningen();

  const globalFormMethods = useFormVerbose<GlobalFormSchema>({
    resolver: zodResolver(globalFormSchema),
    values: responseToGlobalInformationForm(
      relatedGlobalInformation,
      prevInfrastructureEvent
        ? {
            uuid: prevInfrastructureEvent.uuid,
            incidentId: prevInfrastructureEvent.incidentId ?? "",
            texts: {
              NOB: {
                title: prevInfrastructureEvent.stretchName,
                description:
                  prevInfrastructureEvent.message ??
                  t("Kunne ikke finne distribusjonstekst"),
              },
            },
            visibility: {
              type: "OPEN_ENDED",
              fromTime: getCurrentCalendarDateTime(),
            },
          }
        : undefined,
    ),
  });

  const {
    mutateAsync: postInfraInfo,
    isPending: isPostInfrastructureRequestPending,
    isSuccess: isPostInfrastructureRequestSuccess,
    isError: isPostInfrastructureRequestError,
    data: postInfrastructureRequestData,
  } = usePostInfrastructureEvent(prevInfrastructureEvent?.uuid);

  const {
    mutate: postGlobalInfo,
    isPending: isPostGlobalRequestPending,
    isSuccess: isPostGlobalRequestSuccess,
    isError: isPostGlobalRequestError,
  } = usePostGlobalInfo(
    relatedGlobalInformation?.countryCode ?? countryCodeForRole,
    relatedGlobalInformation?.uuid,
  );

  const { mutate: depublishGlobalInfo } = useDepublishInformation();

  const [shouldPostGlobalInfo, setShouldPostGlobalInfo] = useState(false);
  const [shouldDepublishGlobalInfo, setShouldDepublishGlobalInfo] =
    useState(false);
  const [isMajorEvent, setIsMajorEvent] = useState(false);

  const {
    handleSubmit: handleInfrastructureSubmit,
    trigger: triggerInfrastructureValidation,
  } = infrastructureFormMethods;

  const { handleSubmit: handleGlobalSubmit, trigger: triggerGlobalValidation } =
    globalFormMethods;

  const openEditFields = useOpenEditFields((state) => state.openEditFields);

  const onSubmit = handleInfrastructureSubmit((data) => {
    const requestBody = formToRequest(
      data.infrastructureForm,
      countryCodeForRole,
    );
    void postInfraInfo(requestBody, {
      onSuccess: (infrastructureResponse) => {
        const form = data.infrastructureForm;
        const osloSIsClosed =
          legsIncludeStop(form.affectedLegs, OSL) &&
          isInfrastructureStretchClosedEvent(form.type);
        const minimumThirtyMinutes =
          form.durations.type === "OPEN_ENDED" &&
          Number(form.durations.extension_time_in_minutes) >= 30;

        if (osloSIsClosed && minimumThirtyMinutes && !isEditMode) {
          setIsMajorEvent(true);
        }

        const uuidHasChanged =
          prevInfrastructureEvent?.uuid !== infrastructureResponse.uuid;
        if (
          showEvaluationForm &&
          (createEvaluationForm || (uuidHasChanged && hasEvaluationForm))
        ) {
          const evaluationFormUuid =
            prevInfrastructureEvent?.internalMessages?.find(
              (message) => !!message.evaluationForm,
            )?.uuid;
          const internalMessageFormField: InternalMessagesFormField = [
            {
              uuid: evaluationFormUuid,
              message: "Tilbakemeldingsskjema til togpersonell",
              contexts: contextsForEvaluationForm,
            },
          ];
          const evaluationFormUrl = buildEvaluationFormUrl(
            infrastructureResponse.incidentId,
            form,
          );
          void createInternalMessage(
            internalMessageFormField,
            infrastructureResponse,
            undefined,
            { url: evaluationFormUrl },
          );
        }
      },
    }).then((response) =>
      onInternalMessagesSubmit(response, prevInfrastructureEvent?.uuid),
    );
  });

  const onGlobalEventSubmit = handleGlobalSubmit((globalFormData) =>
    postGlobalInfo(
      formToGlobalInformationRequest(
        globalFormData.globalForm,
        prevInfrastructureEvent?.incidentId ?? undefined,
      ),
    ),
  );

  if (stretchBuilderModalState.type !== "infrastructure") return null;

  if (isMajorEvent && isPostInfrastructureRequestSuccess) {
    return (
      <MajorEventWarning
        response={postInfrastructureRequestData}
        onClose={closeAndResetStretchBuilder}
      />
    );
  }

  const changedInfrastructureFields =
    infrastructureFormMethods.formState.dirtyFields.infrastructureForm;
  const changedFieldNames = changedInfrastructureFields
    ? Object.keys(changedInfrastructureFields)
    : [];

  const isChangedText = changedFieldNames.some((field) =>
    FIELDS_AFFECTING_TEXT.includes(field),
  );

  return (
    <FormProvider {...infrastructureFormMethods}>
      {!stretchBuilderModalState.stretchBuilderMode ? (
        <ActionModal
          title={`${isEditMode ? t("Endre") : t("Opprett")} ${t("infrastrukturhendelse")}`}
          actionTitle={`${isEditMode ? t("Endre") : t("Opprett")} ${t("hendelse")}`}
          onClose={() => {
            if (!isMajorEvent) closeAndResetStretchBuilder();
          }}
          onSubmit={async () => {
            const infraIsValid = await triggerInfrastructureValidation();
            const globalIsValid =
              shouldPostGlobalInfo && (await triggerGlobalValidation());
            if (infraIsValid) {
              if (globalIsValid) {
                void onGlobalEventSubmit();
              }
              if (shouldDepublishGlobalInfo && relatedGlobalInformation) {
                depublishGlobalInfo(relatedGlobalInformation.uuid);
              }
              void onSubmit();
            }
          }}
          loading={
            isPostInfrastructureRequestPending || isPostGlobalRequestPending
          }
          isSuccess={
            isPostInfrastructureRequestSuccess &&
            (shouldPostGlobalInfo ? isPostGlobalRequestSuccess : true)
          }
          isError={isPostInfrastructureRequestError || isPostGlobalRequestError}
          isSubmitDisabled={openEditFields.length > 0}
          successMessage={
            isEditMode ? t("Hendelse endret") : t("Hendelse opprettet")
          }
          failureMessage={`${
            isEditMode
              ? t("Kunne ikke endre hendelse.")
              : t("Kunne ikke opprette hendelse.")
          } ${t("Prøv på nytt, eller kontakt IT hvis feilen vedvarer.")}`}
          internalMessagesFormMethods={internalMessagesFormMethods}
          evaluationForm={
            showEvaluationForm
              ? {
                  checked: createEvaluationForm,
                  setChecked: setCreateEvaluationForm,
                  isDisabled: hasEvaluationForm,
                }
              : undefined
          }
        >
          <Stack gap="24px" width="100%">
            <InfrastructureModalContent
              stretchTitle={
                clickedStops.length < 1 && isEditMode
                  ? prevInfrastructureEvent.stretchName
                  : getStretchName(
                      selectedLegs,
                      clickedStops,
                      allStopsSelected,
                    ).join(", ")
              }
            />
            {relatedGlobalInformation && isChangedText && (
              <DropsStaticAlert variant="important" title="OBS!">
                <Text>
                  {t(
                    "Du har endret infrastrukturhendelsen på en måte som kanskje påvirker den globale meldingen",
                  )}
                </Text>
              </DropsStaticAlert>
            )}
            {isEditMode &&
              !isPlannedInfraStructureEvent(
                infrastructureFormMethods.getValues("infrastructureForm.type"),
              ) && (
                <>
                  {globaInfoIsPending && globalInfoIsFetching && (
                    <Skeleton height="36px" width="100%" />
                  )}
                  {globalInfoIsError && (
                    <DropsStaticAlert variant="error">
                      {t("Kunne ikke hente global driftsmelding")}
                    </DropsStaticAlert>
                  )}
                  {globalInfoIsSuccess && !isDriftledningen() && (
                    <ReferencedGlobalEvent
                      globalEventFormMethods={globalFormMethods}
                      shouldPost={shouldPostGlobalInfo}
                      setShouldPost={setShouldPostGlobalInfo}
                      shouldDepublish={shouldDepublishGlobalInfo}
                      setShouldDepublish={setShouldDepublishGlobalInfo}
                      relatedGlobalInformation={relatedGlobalInformation}
                    />
                  )}
                </>
              )}
          </Stack>
        </ActionModal>
      ) : (
        <div />
      )}
    </FormProvider>
  );
};
